import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Attribute, ProductResult } from '../../models/product-result.model';
import { AppConfigService } from '../../services/app-config-service/app-config.service';
import { AssessmentService } from '../../services/assessment-service/assessment.service';
import { UrlConfigService } from '../../services/url-config-service/url-config.service';
import { UserContextService } from '../../services/user-context-service/user-context.service';

@Component({
  selector: 'app-result',
  templateUrl: './result.component.html',
  imports: [TranslateModule, RouterModule],
})
export class ResultComponent implements OnInit, OnDestroy {
  productResults: ProductResult[] = [];
  showResultToUser: boolean;
  documentCreationEnabled: boolean;
  docUrl: string = '';
  unsubscribe$ = new Subject();
  validAttributeIds: string[];
  insuredPersonFullName: string;
  showGoToInitialInformationPageButton: boolean;

  constructor(
    private readonly assessmentService: AssessmentService,
    private readonly appConfig: AppConfigService,
    private readonly urlConfigService: UrlConfigService,
    private readonly userContextService: UserContextService,
    private readonly router: Router
  ) {
    this.showResultToUser = this.appConfig.featureToggles.showResultToUser;
    this.documentCreationEnabled = this.appConfig.featureToggles.documentCreationEnabled;
    this.validAttributeIds = this.appConfig.configData.resultPage.captionAndValue.concat(
      this.appConfig.configData.resultPage.durationMonth,
      this.appConfig.configData.resultPage.percentageLoading,
      this.appConfig.configData.resultPage.textValueOnly,
      this.appConfig.configData.resultPage.listStructure,
      this.appConfig.configData.resultPage.objectStructure
    );
    this.validAttributeIds.push(
      this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.duration,
      this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.loading
    );
    this.showGoToInitialInformationPageButton = this.appConfig.configData.resultPage.showNavigationButtonToInformationPage;
  }

  ngOnInit(): void {
    const currentAssessmentWrapperId = this.assessmentService.getCurrentAssessmentWrapperId();
    if (currentAssessmentWrapperId) {
      this.assessmentService
        .getResult(currentAssessmentWrapperId)
        .pipe(
          takeUntil(this.unsubscribe$),
          tap(productResults => {
            this.productResults = productResults;
            this.docUrl = this.urlConfigService.getDocumentServiceDocUrl(currentAssessmentWrapperId);
          }),
          switchMap(() => {
            return this.assessmentService.postProcess(currentAssessmentWrapperId);
          })
        )
        .subscribe();

      this.insuredPersonFullName = this.userContextService.getInsuredPersonFullNameForAssessmentWrapper(currentAssessmentWrapperId);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  showDetails(attributes: Attribute[]): boolean {
    return !!attributes.find(e => this.validAttributeIds.includes(e.id));
  }

  filterAttribute(attributes: Attribute[], id: string): Attribute[] {
    return attributes.filter(e => e.id === id);
  }

  hasDuration(attributes: Attribute[]): boolean {
    return !!attributes.find(e => e.id === 'FlatExtraDuration');
  }

  isTextValueOnlyTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.textValueOnly.includes(currentAttribute.id)
    );
  }

  isCaptionAndValueTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.captionAndValue.includes(currentAttribute.id)
    );
  }

  isPercentageLoadingTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.percentageLoading.includes(currentAttribute.id)
    );
  }

  isDurationMonthTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.durationMonth.includes(currentAttribute.id)
    );
  }

  isPerMilleLoadingTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.perMilleLoading.includes(currentAttribute.id)
    );
  }

  isFlatExtraLoadingWithDuration(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute.id === this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.duration &&
      this.isFlatExtraLoadingWithDurationInsideAttributes(attributes)
    );
  }

  isListStructureTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.listStructure.includes(currentAttribute.id)
    );
  }

  isObjectStructureTemplate(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return (
      currentAttribute &&
      this.isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes, currentAttribute) &&
      this.appConfig.configData.resultPage.objectStructure.includes(currentAttribute.id)
    );
  }

  isNotAttributePartOfFlatExtraLoadingWithDurationConfig(attributes: Attribute[], currentAttribute: Attribute): boolean {
    return !(
      this.isFlatExtraLoadingWithDurationInsideAttributes(attributes) &&
      (currentAttribute.id === this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.loading ||
        currentAttribute.id === this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.duration)
    );
  }

  isFlatExtraLoadingWithDurationInsideAttributes(attributes: Attribute[]): boolean {
    return (
      this.filterAttribute(attributes, this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.loading).length === 1 &&
      this.filterAttribute(attributes, this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.duration).length === 1
    );
  }

  getFlatExtraLoadingWithDurationCaption(attributes: Attribute[]) {
    return attributes.filter(e => e.id === this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.loading).pop().value;
  }

  getFlatExtraLoadingWithDurationValue(attributes: Attribute[]) {
    return attributes.filter(e => e.id === this.appConfig.configData.resultPage.flatExtraLoadingWithDuration.duration).pop().value;
  }

  goToInitialInformationPage(): void {
    this.router.navigate(['/initial-information']);
  }
}
